import { inView, motionValue, scroll, styleEffect, transform, transformValue } from 'motion';
import { isHtmlElement } from '../utils';
import { motionArg } from '../utils/motion';
import { cancelIdleCallback, requestIdleCallback } from '../utils/dom';
import { isLowPowerDevice, isReducedMotion } from '../utils/device';
import { getModifierValue } from '../utils/modifiers';
import { onFirstLayout } from '../utils/layout-measure';
/** Scroll range for an element: progress 0 = element top at viewport bottom, 1 = element bottom at viewport top. */
function getElementScrollRange(el) {
    const viewportHeight = window.innerHeight;
    const rect = el.getBoundingClientRect();
    const elementTopDoc = rect.top + window.scrollY;
    const elementHeight = rect.height;
    const scrollYStart = elementTopDoc - viewportHeight;
    const scrollYEnd = elementTopDoc + elementHeight;
    return { scrollYStart, scrollRange: scrollYEnd - scrollYStart };
}
/**
 * Creates a scroll callback that maps page scroll to element-based progress [0,1] and applies
 * initial-render offset so the parallax doesn't jump when the element first enters view.
 */
function createElementScrollListener(options) {
    const { scrollYStart, scrollRange, scrollValue, onAnchored } = options;
    let initialProgress = 0;
    let hasCapturedInitial = false;
    let hasAnchored = false;
    return (_progress, info) => {
        const scrollY = info.y.current;
        const elementProgress = Math.max(0, Math.min(1, (scrollY - scrollYStart) / scrollRange));
        if (!hasCapturedInitial && elementProgress !== 0) {
            initialProgress = elementProgress;
            hasCapturedInitial = true;
        }
        else {
            scrollValue.set(elementProgress - initialProgress);
        }
        if (!hasAnchored) {
            hasAnchored = true;
            onAnchored === null || onAnchored === void 0 ? void 0 : onAnchored();
        }
    };
}
/**
 * Adds a parallax effect on an element based on the scroll position of its root.
 *
 * ## Directives
 *
 * - `x-parallax` - Root element. Maps the page scroll across the root's bounds to a shared progress value.
 * - `x-parallax:element` - Element translated by the root's progress.
 *
 * ## Element modifiers
 *
 * - `.distance.<n>px` / `.distance.<n>%` / `.distance.<n>vh` - Travel distance (default `10px`). `%` is a CSS translate percentage, relative to the element's own size.
 * - `.reverse` - Moves the element against the scroll direction.
 * - `.horizontal` - Translates on the x axis instead of the y axis.
 *
 * Does nothing when the user prefers reduced motion or the device reports low power.
 * Parallax elements should have `will-change: transform` to improve performance.
 *
 * `x-parallax:element` with no parent `x-parallax` does nothing, so a snippet can carry the
 * directive and still be used outside a parallax root.
 *
 * @example
 *
 * <div
 *   class="relative flex justify-center items-center overflow-hidden h-[200px]"
 *   x-parallax
 * >
 *   <div
 *     class="absolute will-change-transform flex justify-center items-center min-h-full w-full h-[180%] text-white"
 *     x-parallax:element.distance.200px
 *   >
 *     Parallax child
 *   </div>
 * </div>
 */
export default function (Alpine) {
    Alpine.directive('parallax', ($el, data, { cleanup, effect, evaluate }) => {
        if (!isHtmlElement($el)) {
            throw new Error(`Expected HTMLElement but got ${$el}`);
        }
        if (data.value === 'element') {
            handleElement($el, Alpine, data);
        }
        else if (data.value === null) {
            handleRoot($el, Alpine);
        }
        else {
            throw new Error(`Unknown directive: x-parallax:${data.value}`);
        }
    }).before('bind');
    /**
     * Magic providing access to the nearest parallax root's scroll state.
     * Use within a parallax root (or any descendant) to read the shared scroll progress.
     *
     * @example
     * <div x-parallax>
     *   <div x-effect="$parallax.scrollValue?.get() && ...">...</div>
     * </div>
     */
    Alpine.magic('parallax', ($el) => {
        const $data = Alpine.$data($el);
        return {
            get scrollValue() {
                return Alpine.raw($data.__parallaxElement_scrollValue);
            },
        };
    });
}
function handleElement($el, Alpine, data) {
    let parallaxDistance = 10;
    let parallaxUnit = 'px';
    const reverse = data.modifiers.includes('reverse');
    const horizontal = data.modifiers.includes('horizontal');
    const rawDistanceValue = getModifierValue(data.modifiers, 'distance');
    if (rawDistanceValue != null) {
        // Support x-parallax:element.200px which will move the element up or down by 200px
        let pxMatch = rawDistanceValue.match(/(-?[0-9]+)px/);
        if (pxMatch) {
            parallaxDistance = Number(pxMatch[1]);
            parallaxUnit = 'px';
        }
        let percentageMatch = rawDistanceValue.match(/(-?[0-9]+)%/);
        if (percentageMatch) {
            parallaxDistance = Number(percentageMatch[1]);
            parallaxUnit = '%';
        }
        let vhMatch = rawDistanceValue.match(/(-?[0-9]+)vh/);
        if (vhMatch) {
            parallaxDistance = Number(vhMatch[1]);
            parallaxUnit = 'vh';
        }
    }
    Alpine.bind($el, {
        ['x-data']() {
            return {
                __parallaxElement_styleEffect: null,
                __parallaxElement_cancelInView: null,
                init() {
                    // No parent x-parallax means no scroll value to follow, so do nothing. Markup that
                    // shares a snippet between parallax and non-parallax placements relies on this.
                    if (!this.__rootEl)
                        return;
                    if (isReducedMotion() || isLowPowerDevice())
                        return;
                    this.$nextTick(() => {
                        // Wait for the element to be in view before measuring / parallaxing so that:
                        // 1. Height measurements are correct. If element is hidden, height will be 0.
                        // 2. The element is initially aligned to the top/bottom of the container so that the image has room to move as the page scrolls.
                        // 3. Not running unnecessary parallax animations if the element is hidden.
                        inView($el, () => {
                            this.__parallaxElement_setupScroll();
                        });
                    });
                },
                __parallaxElement_setupScroll() {
                    // init() already bails when there's no parent x-parallax; this stays as a defensive guard.
                    if (!this.__rootEl)
                        return;
                    if (this.__parallaxElement_styleEffect)
                        return;
                    const scrollValue = this.__parallaxElement_scrollValue;
                    const parallaxBounds = [
                        `${parallaxDistance}${parallaxUnit}`,
                        `0${parallaxUnit}`,
                        `${-1 * parallaxDistance}${parallaxUnit}`,
                    ];
                    const scrollTransform = transform(reverse ? [1, 0, -1] : [-1, 0, 1], parallaxBounds);
                    $el.setAttribute('data-parallax-active', 'true');
                    Alpine.mutateDom(() => {
                        this.__parallaxElement_styleEffect = styleEffect($el, {
                            [horizontal ? 'x' : 'y']: transformValue(() => scrollTransform(scrollValue.get())),
                        });
                    });
                },
                destroy() {
                    var _a, _b;
                    (_a = this.__parallaxElement_cancelInView) === null || _a === void 0 ? void 0 : _a.call(this);
                    this.__parallaxElement_cancelInView = null;
                    (_b = this.__parallaxElement_styleEffect) === null || _b === void 0 ? void 0 : _b.call(this);
                    this.__parallaxElement_styleEffect = null;
                    $el.removeAttribute('data-parallax-active');
                },
            };
        },
    });
}
function handleRoot($el, Alpine) {
    Alpine.bind($el, {
        ['x-data']() {
            return {
                __rootEl: $el,
                __parallaxElement_cancelInView: null,
                __parallaxElement_idleCallbackId: null,
                __parallaxElement_cancelFirstLayout: null,
                __parallaxElement_motionCancelFn: null,
                __parallaxElement_scrollValue: motionValue(0),
                init() {
                    if (isReducedMotion() || isLowPowerDevice())
                        return;
                    this.$nextTick(() => {
                        this.__parallaxElement_idleCallbackId = requestIdleCallback(() => {
                            this.__parallaxElement_idleCallbackId = null;
                            // setupScroll measures the element; onFirstLayout provides a clean-layout
                            // read point (and waits for a box if the element isn't rendered yet).
                            this.__parallaxElement_cancelFirstLayout = onFirstLayout($el, () => {
                                this.__parallaxElement_cancelFirstLayout = null;
                                this.__parallaxElement_setupScroll();
                            });
                        }, { timeout: 2000 });
                    });
                },
                __parallaxElement_setupScroll() {
                    if (this.__parallaxElement_motionCancelFn)
                        return;
                    const { scrollYStart, scrollRange } = getElementScrollRange($el);
                    this.__parallaxElement_motionCancelFn = scroll(createElementScrollListener({
                        scrollYStart,
                        scrollRange,
                        scrollValue: motionArg(this.__parallaxElement_scrollValue),
                        // The listener anchors its zero point to the first scroll position it observes, and
                        // Motion delivers that first reading a frame after `scroll()` returns. Flagging it
                        // from inside the listener lets anything that scrolls the page programmatically
                        // (tests, deep links) wait until the anchor is settled before moving.
                        onAnchored: () => $el.setAttribute('data-parallax-active', 'true'),
                    }), { offset: ['start end', 'end start'] });
                },
                destroy() {
                    var _a, _b, _c;
                    if (this.__parallaxElement_idleCallbackId !== null) {
                        cancelIdleCallback(this.__parallaxElement_idleCallbackId);
                        this.__parallaxElement_idleCallbackId = null;
                    }
                    (_a = this.__parallaxElement_cancelFirstLayout) === null || _a === void 0 ? void 0 : _a.call(this);
                    this.__parallaxElement_cancelFirstLayout = null;
                    (_b = this.__parallaxElement_cancelInView) === null || _b === void 0 ? void 0 : _b.call(this);
                    this.__parallaxElement_cancelInView = null;
                    (_c = this.__parallaxElement_motionCancelFn) === null || _c === void 0 ? void 0 : _c.call(this);
                    this.__parallaxElement_motionCancelFn = null;
                    $el.removeAttribute('data-parallax-active');
                },
            };
        },
    });
}
